import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { TopBar } from "../components/TopBar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { api, STATUS_LABELS } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { ScanLine, Camera, CircleX, Search, ArrowRight, RefreshCw, PackageCheck, Smartphone } from "lucide-react";
import { CreateRecordDialog } from "../components/CreateRecordDialog";
import { useAuth } from "../context/AuthContext";
import { StepBar, buildStagesFromRecord } from "../components/ProgressTimeline";

export default function ScannerPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [manualQuery, setManualQuery] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [thirdParties, setThirdParties] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const scannerRef = useRef(null);

  useEffect(() => {
    api.get("/third-parties").then((r) => setThirdParties(r.data)).catch(() => {});
    return () => {
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doSearch = async (q) => {
    const query = (q || manualQuery).trim();
    if (!query) return;
    setSearched(true);
    try {
      // First search local records
      const local = await api.get("/bath-records", { params: { search: query } });
      // Then Brightpearl lookup
      const bp = await api.get("/brightpearl/lookup", { params: { q: query } });
      setResults(buildMerged(local.data, bp.data.results));
    } catch (e) {
      toast.error("Lookup failed");
    }
  };

  const buildMerged = (local, bp) => {
    const out = local.map((r) => ({ type: "local", record: r }));
    bp.forEach((b) => {
      if (!b.local_record) out.push({ type: "brightpearl", record: b });
    });
    return out;
  };

  const startScan = async () => {
    setScanning(true);
    // Wait for React to render the scanner region into the DOM
    await new Promise((r) => setTimeout(r, 60));
    const elementId = "qr-scanner-region";

    try {
      if (!window.isSecureContext) {
        throw new Error("INSECURE_CONTEXT");
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("NO_MEDIA_DEVICES");
      }
      if (!document.getElementById(elementId)) {
        throw new Error("CONTAINER_MISSING");
      }

      if (!scannerRef.current) {
        scannerRef.current = new Html5Qrcode(elementId, { verbose: false });
      }

      const config = { fps: 10, qrbox: { width: 280, height: 180 }, aspectRatio: 1.7777778 };
      const onDecoded = async (decodedText) => {
        await stopScan();
        setManualQuery(decodedText);
        toast.success(`Scanned: ${decodedText}`);
        doSearch(decodedText);
      };

      try {
        // Prefer rear camera on mobile
        await scannerRef.current.start({ facingMode: { exact: "environment" } }, config, onDecoded, () => {});
      } catch (envErr) {
        // Fallback 1: relaxed environment hint (desktops or single-camera devices)
        try {
          await scannerRef.current.start({ facingMode: "environment" }, config, onDecoded, () => {});
        } catch (relaxedErr) {
          // Fallback 2: pick a specific camera id (desktop / front-only devices)
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw new Error("NO_CAMERA");
          const camId = cams[cams.length - 1].id; // last camera is usually rear on phones
          await scannerRef.current.start({ deviceId: { exact: camId } }, config, onDecoded, () => {});
        }
      }
    } catch (e) {
      setScanning(false);
      const raw = e?.message || e?.name || String(e);
      console.error("[Scanner] start failed:", e);
      let msg = `Camera unavailable: ${raw.slice(0, 100)}`;
      if (raw === "INSECURE_CONTEXT") msg = "Camera requires HTTPS. Open the app over a secure connection.";
      else if (raw === "NO_MEDIA_DEVICES") msg = "This browser does not support camera access.";
      else if (raw === "CONTAINER_MISSING") msg = "Scanner container failed to mount. Refresh the page.";
      else if (raw === "NO_CAMERA") msg = "No camera detected on this device.";
      else if (/NotAllowed|Permission|denied/i.test(raw)) msg = "Camera permission denied. Allow access in your browser settings.";
      else if (/NotFound|DevicesNotFound/i.test(raw)) msg = "No camera detected on this device.";
      else if (/NotReadable|TrackStart/i.test(raw)) msg = "Camera is in use by another app. Close other apps using the camera.";
      toast.error(msg);
    }
  };

  const stopScan = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
      }
    } catch {}
    setScanning(false);
  };

  const importFromBP = (bp) => {
    setPrefill({
      brightpearl_id: bp.brightpearl_id,
      supplier_po: bp.supplier_po,
      internal_po: bp.internal_po,
      sales_order: bp.sales_order,
      customer_ref: bp.customer_ref,
      product_sku: bp.product_sku,
      description: bp.description,
      barcode: bp.barcode,
      status: bp.goods_in_status === "received" ? "received" : "awaiting_arrival",
      location: bp.warehouse,
    });
    setShowCreate(true);
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <TopBar />

      <main className="mx-auto max-w-5xl px-4 sm:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Smartphone className="h-4 w-4 text-zinc-500 sm:hidden" />
            <h1 className="font-heading text-2xl sm:text-4xl font-extrabold tracking-tighter text-zinc-950">
              Scan / Lookup
            </h1>
          </div>
          <p className="text-sm text-zinc-600">
            Scan a barcode or enter a PO, SO, SKU or customer reference. We'll check local records and Brightpearl.
          </p>
        </div>

        {/* Scan + manual entry */}
        <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mb-6">
          <div className="bg-white border border-zinc-200 rounded-md p-3 sm:p-4 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-bold text-base tracking-tight text-zinc-950 flex items-center gap-2">
                <Camera className="h-4 w-4" /> Camera Scanner
              </h3>
              {scanning ? (
                <Button size="sm" variant="outline" onClick={stopScan} data-testid="stop-scan-button">
                  <CircleX className="h-3.5 w-3.5 mr-1.5" /> Stop
                </Button>
              ) : (
                <Button size="sm" onClick={startScan} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="scan-barcode-button">
                  <ScanLine className="h-3.5 w-3.5 mr-1.5" /> Start scan
                </Button>
              )}
            </div>
            <div className="relative bg-zinc-100 rounded-md overflow-hidden min-h-[260px] sm:min-h-[260px]">
              <div id="qr-scanner-region" className={scanning ? "block" : "opacity-0 pointer-events-none h-0"} />
              {!scanning && (
                <div className="absolute inset-0 flex items-center justify-center text-zinc-400 text-xs text-center p-6">
                  <div>
                    <Camera className="h-8 w-8 mx-auto mb-2 text-zinc-300" strokeWidth={1.5} />
                    Tap "Start scan" to use the device camera.
                  </div>
                </div>
              )}
            </div>
            {scanning && (
              <p className="text-[11px] text-zinc-500 text-center mt-2 leading-relaxed">
                Hold the barcode steady inside the box. We'll detect it automatically.
              </p>
            )}
          </div>

          <div className="bg-white border border-zinc-200 rounded-md p-3 sm:p-4 shadow-sm">
            <h3 className="font-heading font-bold text-base tracking-tight text-zinc-950 mb-3 flex items-center gap-2">
              <Search className="h-4 w-4" /> Manual Entry
            </h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                doSearch();
              }}
              className="space-y-3"
            >
              <Input
                value={manualQuery}
                onChange={(e) => setManualQuery(e.target.value)}
                placeholder="Enter PO, SO, SKU, barcode or customer ref"
                className="h-12 sm:h-11 font-mono text-base sm:text-sm"
                data-testid="manual-lookup-input"
                inputMode="search"
                autoFocus
              />
              <Button type="submit" className="w-full h-12 sm:h-11 bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="manual-lookup-button">
                <Search className="h-4 w-4 mr-2" /> Lookup
              </Button>
            </form>
            <p className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
              USB / Bluetooth barcode readers act as keyboards — just place focus here and scan.
            </p>
          </div>
        </div>

        {/* Results */}
        {searched && (
          <div className="bg-white border border-zinc-200 rounded-md shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="font-heading font-bold text-base tracking-tight text-zinc-950">
                Results for "{manualQuery}"
              </h3>
              <span className="text-xs text-zinc-500">{results.length} match{results.length === 1 ? "" : "es"}</span>
            </div>
            {results.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500">
                No matches found in local records or Brightpearl.
              </div>
            )}
            <ul>
              {results.map((row, i) => {
                const r = row.record;
                const isLocal = row.type === "local";
                return (
                  <li
                    key={i}
                    className="border-b border-zinc-100 last:border-b-0 px-4 py-3 hover:bg-zinc-50 transition-colors flex flex-wrap items-center justify-between gap-3"
                    data-testid={`result-row-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${isLocal ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-blue-50 text-blue-700 border border-blue-200"}`}>
                          {isLocal ? "Local record" : "Brightpearl only"}
                        </span>
                        {isLocal && <StatusBadge status={r.status} />}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                        <span className="font-mono font-semibold text-zinc-950">{r.supplier_po}</span>
                        <span className="font-mono text-zinc-700">{r.internal_po || ""}</span>
                        <span className="font-mono text-zinc-700">{r.sales_order || ""}</span>
                        <span className="font-mono text-zinc-500">{r.product_sku}</span>
                      </div>
                      <div className="text-xs text-zinc-600 mt-0.5 truncate max-w-2xl">{r.description}</div>
                      {r.customer_ref && r.customer_ref !== "[Restricted]" && (
                        <div className="text-[11px] text-zinc-500">{r.customer_ref}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {isLocal ? (
                        <>
                          {/* Mobile-friendly quick "receive" action for awaiting baths */}
                          {r.status === "awaiting_arrival" && (user?.role === "warehouse" || user?.role === "admin") && (
                            <Button
                              size="sm"
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  await api.patch(`/bath-records/${r.id}`, { status: "received" });
                                  toast.success("Marked as received");
                                  doSearch();
                                } catch {
                                  toast.error("Update failed");
                                }
                              }}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9"
                              data-testid={`quick-receive-${r.id}`}
                            >
                              <PackageCheck className="h-3.5 w-3.5 mr-1.5" /> Mark received
                            </Button>
                          )}
                          <Button size="sm" onClick={() => navigate(`/bath/${r.id}`)} className="h-9" data-testid={`open-record-${r.id}`}>
                            Open <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                          </Button>
                        </>
                      ) : (
                        user?.role !== "third_party" && user?.role !== "read_only" && (
                          <Button size="sm" variant="outline" onClick={() => importFromBP(r)} data-testid={`import-bp-${r.brightpearl_id}`}>
                            Import to tracker
                          </Button>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </main>

      <CreateRecordDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        thirdParties={thirdParties}
        prefill={prefill}
        onCreated={() => {
          setPrefill(null);
          doSearch();
        }}
      />
    </div>
  );
}
